import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Typography,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  Tooltip,
  Chip,
  Checkbox,
  FormControlLabel,
  Collapse,
  Stack,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  LayersRounded as FloorIcon,
  PictureAsPdfRounded as PdfIcon,
  KeyboardArrowDownRounded as ExpandIcon,
  KeyboardArrowRightRounded as CollapseIcon,
} from "@mui/icons-material";
import axios from "axios";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";
import { usePawnMode } from "../../Context/PawnModeContext";
import {
  reportPageSx,
  reportFixedSx,
  reportResultsCardSx,
  reportTableSx,
} from "./reportLayout";
import ReportSortControl, { DEFAULT_SORT, sortRows } from "./reportSort";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

// ─── Blue/White Design Tokens (shared with the other pawn reports) ───────────
const BLUE = "#2563EB";
const BLUE_DARK = "#1D4ED8";
const BLUE_PALE = "#EFF6FF";
const INK = "#0F172A";
const INK_SOFT = "#334155";
const INK_MUTED = "#64748B";
const BORDER = "#E2E8F0";
const SURFACE = "#FFFFFF";
const SUCCESS = "#059669";

const TODAY = () => dayjs().format("YYYY-MM-DD");

// ── Formatting helpers ──────────────────────────────────────────────────────
// Zero/blank weights print as EMPTY, matching the old register where a floor
// holding nothing leaves its weight cells blank instead of showing "0.000".
const fmtWeight = (v) => {
  const n = parseFloat(v);
  if (!n) return "";
  return n.toLocaleString("en-IN", {
    maximumFractionDigits: 3,
    minimumFractionDigits: 3,
  });
};
// Same as above but never blank — used for the totals row and the drill-down.
const fmtWeightAlways = (v) => {
  const n = parseFloat(v) || 0;
  return n.toLocaleString("en-IN", {
    maximumFractionDigits: 3,
    minimumFractionDigits: 3,
  });
};
const fmtINR = (v) => {
  const n = parseFloat(v);
  if (isNaN(n)) return "—";
  return n.toLocaleString("en-IN", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });
};

// ─── Sorting ─────────────────────────────────────────────────────────────────
// A row here is a FLOOR, not a record, so the usual date / customer / voucher
// options have nothing to read — the register sorts on what a floor actually
// carries instead. (The drill-down under each floor keeps its own order.)
const SORT_OPTIONS = [
  { key: "floor", label: "Floor Name" },
  { key: "tickets", label: "No of Pawn Ticket" },
  { key: "weight", label: "Total Weight" },
  { key: "amount", label: "Loan Amount" },
];
const ROW_SORT = {
  floor: (f) => f.floor_name,
  tickets: (f) => f.ticket_count || 0,
  weight: (f) => f.total_wt || 0,
  amount: (f) => f.loan_amount || 0,
};

/**
 * Floorwise summary — the strong-room register.
 *
 * Every ACTIVE floor of the store is listed (even the empty ones, exactly like
 * the printed register) with the loans lying on it AS ON a date: pledged on or
 * before that date and not yet redeemed by then. Ticket count, jewel weight,
 * tare, extra tare and total weight per floor; each row expands into the
 * individual pawn tickets sitting on that floor.
 *
 * The aggregation happens server-side (`/api/pawn/floor-summary/:store_id`) —
 * a strong room holds thousands of tickets, so this must never be a per-ticket
 * fan-out from the browser.
 */
const FloorReport = ({ embedded = false }) => {
  const { currentStoreInfo } = useContext(AutoTaskAuthContext) || {};
  const storeId = currentStoreInfo?.id;
  // Pawn mode: 1 = official, 2 = unofficial. Scoped server-side via `mode`.
  const { mode: pawnMode } = usePawnMode() || {};

  // ── Filter state ──────────────────────────────────────────────────────────
  const [asOn, setAsOn] = useState(TODAY());
  const [floorSearch, setFloorSearch] = useState("");
  const [includeSettled, setIncludeSettled] = useState(false);
  const [hideTotal, setHideTotal] = useState(false);
  const [hideEmpty, setHideEmpty] = useState(false);
  const [showAmount, setShowAmount] = useState(false);
  // Row order chosen from the sort button beside View — client-side only.
  const [sort, setSort] = useState(DEFAULT_SORT);

  // ── Data state ────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [floors, setFloors] = useState([]);
  const [expanded, setExpanded] = useState(() => new Set());
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const showSnackbar = useCallback(
    (message, severity = "info") =>
      setSnackbar({ open: true, message, severity }),
    [],
  );

  // ── Load the summary ──────────────────────────────────────────────────────
  const buildReport = useCallback(async () => {
    if (storeId == null) {
      showSnackbar("No store selected.", "warning");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/pawn/floor-summary/${storeId}`,
        {
          params: {
            asOn,
            includeSettled: includeSettled ? 1 : 0,
            mode: Number(pawnMode) === 2 ? 2 : 1,
          },
        },
      );
      if (!(res.data?.success && res.data?.statusCode === 200)) {
        showSnackbar(
          res.data?.statusMessage || "Failed to load floor report.",
          "error",
        );
        setFloors([]);
        return;
      }
      const list = res.data.data?.floors || [];
      setFloors(list);
      setExpanded(new Set());
      if (list.length === 0)
        showSnackbar("No floors configured for this store.", "info");
    } catch (err) {
      showSnackbar(
        err?.response?.data?.statusMessage || "Failed to load floor report.",
        "error",
      );
      setFloors([]);
    } finally {
      setLoading(false);
    }
  }, [storeId, asOn, includeSettled, pawnMode, showSnackbar]);

  // Auto-load on mount, when the store resolves, when the pawn mode flips and
  // whenever the AS-ON date or the settled toggle changes (both change the data
  // source). The floor-name box is a text filter and applies client-side.
  useEffect(() => {
    if (storeId != null) buildReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId, pawnMode, asOn, includeSettled]);

  const handleReset = () => {
    setAsOn(TODAY());
    setFloorSearch("");
    setIncludeSettled(false);
    setHideTotal(false);
    setHideEmpty(false);
    setShowAmount(false);
    setSort(DEFAULT_SORT);
  };

  const toggleRow = (key) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // ── Client-side view filters (name search / hide empty floors) ────────────
  const rows = useMemo(() => {
    const q = floorSearch.trim().toLowerCase();
    const out = floors.filter((f) => {
      if (hideEmpty && !f.ticket_count) return false;
      if (!q) return true;
      return (
        (f.floor_name || "").toLowerCase().includes(q) ||
        (f.floor_quick_no || "").toLowerCase().includes(q)
      );
    });
    // Ordering last, so it applies to whatever survived the filters.
    return sortRows(out, sort, ROW_SORT);
  }, [floors, floorSearch, hideEmpty, sort]);

  // Totals follow what is ON SCREEN, so a filtered view still adds up.
  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, f) => {
          acc.ticket_count += f.ticket_count || 0;
          acc.gross_wt += f.gross_wt || 0;
          acc.tare_wt += f.tare_wt || 0;
          acc.extra_tare_wt += f.extra_tare_wt || 0;
          acc.total_wt += f.total_wt || 0;
          acc.loan_amount += f.loan_amount || 0;
          return acc;
        },
        {
          ticket_count: 0,
          gross_wt: 0,
          tare_wt: 0,
          extra_tare_wt: 0,
          total_wt: 0,
          loan_amount: 0,
        },
      ),
    [rows],
  );

  // ── PDF export — mirrors the printed floorwise register ───────────────────
  const generatePDF = () => {
    if (rows.length === 0) {
      showSnackbar("Nothing to print. Load the report first.", "warning");
      return;
    }

    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 10;

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text(currentStoreInfo?.name || "Floor Report", margin, 14);

    if (currentStoreInfo?.address) {
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.text(String(currentStoreInfo.address), margin, 19);
    }

    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text(
      `FLOORWISE SUMMARY REPORT on ${dayjs(asOn).format("DD-MM-YYYY")}`,
      margin,
      27,
    );

    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text(
      `${totals.ticket_count} ticket${totals.ticket_count === 1 ? "" : "s"}${
        includeSettled ? " | incl. settled" : ""
      } | Generated ${dayjs().format("DD-MM-YYYY HH:mm")}`,
      pageWidth - margin,
      27,
      { align: "right" },
    );

    const head = [
      [
        "Floor",
        "No of Pawn Ticket",
        "Weight of Jewels",
        "Tare Wt.",
        "Extra Tare",
        "Total Wt.",
        ...(showAmount ? ["Loan Amount (Rs)"] : []),
      ],
    ];
    const body = rows.map((f) => [
      f.floor_name,
      f.ticket_count,
      fmtWeight(f.gross_wt),
      fmtWeight(f.tare_wt),
      fmtWeight(f.extra_tare_wt),
      fmtWeight(f.total_wt),
      ...(showAmount ? [f.ticket_count ? fmtINR(f.loan_amount) : ""] : []),
    ]);
    if (!hideTotal)
      body.push(
        [
          "",
          totals.ticket_count,
          fmtWeightAlways(totals.gross_wt),
          fmtWeightAlways(totals.tare_wt),
          fmtWeightAlways(totals.extra_tare_wt),
          fmtWeightAlways(totals.total_wt),
          ...(showAmount ? [fmtINR(totals.loan_amount)] : []),
        ].map((content, i) => ({
          content,
          styles: { fontStyle: "bold", halign: i === 0 ? "left" : "right" },
        })),
      );

    const colStyles = { 0: { halign: "left" } };
    for (let i = 1; i <= (showAmount ? 6 : 5); i++)
      colStyles[i] = { halign: "right" };

    doc.autoTable({
      startY: 32,
      head,
      body,
      theme: "grid",
      styles: {
        fontSize: 8,
        cellPadding: 1.5,
        lineColor: [226, 232, 240],
        lineWidth: 0.1,
        textColor: [15, 23, 42],
        valign: "middle",
      },
      headStyles: {
        fillColor: [37, 99, 235],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        fontSize: 8,
        halign: "center",
        valign: "middle",
      },
      columnStyles: colStyles,
      margin: { left: margin, right: margin },
    });

    doc.save(`Floor_Report_${dayjs(asOn).format("YYYY-MM-DD")}.pdf`);
  };

  // ── Cell styling (shared with the other report screens) ───────────────────
  const headCellSx = {
    bgcolor: "#F8FAFC",
    color: INK_MUTED,
    fontSize: "0.68rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    borderBottom: `1px solid ${BORDER}`,
    whiteSpace: "nowrap",
  };
  const cellSx = {
    fontSize: "0.8rem",
    color: INK_SOFT,
    borderBottom: `1px solid ${BORDER}`,
    py: 1,
    verticalAlign: "middle",
  };
  const footBase = {
    position: "sticky",
    bottom: 0,
    bgcolor: "#F8FAFC",
    fontWeight: 800,
    color: INK,
    fontSize: "0.82rem",
    borderTop: `2px solid ${BORDER}`,
  };
  const colCount = showAmount ? 8 : 7; // incl. the expand-arrow column

  return (
    <Box sx={reportPageSx(embedded)}>
      {/* ── Header (hidden when embedded in the unified Reports screen) ── */}
      {!embedded && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 2,
            ...reportFixedSx,
          }}
        >
          <Box
            sx={{
              width: { xs: 36, sm: 42 },
              height: { xs: 36, sm: 42 },
              flexShrink: 0,
              borderRadius: 2,
              bgcolor: BLUE,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              boxShadow: "0 4px 12px rgba(37,99,235,0.35)",
            }}
          >
            <FloorIcon />
          </Box>
          <Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: "1.05rem", sm: "1.25rem" },
                color: INK,
              }}
            >
              Floor Report
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: INK_MUTED }}>
              {currentStoreInfo?.name ? `${currentStoreInfo.name}` : ""}
            </Typography>
          </Box>
        </Box>
      )}

      {/* ── Filters ── */}
      <Card
        elevation={0}
        sx={{
          border: `1px solid ${BORDER}`,
          borderRadius: 3,
          mb: 2,
          bgcolor: SURFACE,
          ...reportFixedSx,
        }}
      >
        <CardContent
          sx={{
            px: { xs: 1.5, md: 2 },
            py: { xs: 1.5, md: 2 },
            "&:last-child": { pb: { xs: 1.5, md: 2 } },
          }}
        >
          <Grid container spacing={{ xs: 1.5, md: 2 }} alignItems="flex-end">
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="As On"
                type="date"
                size="small"
                fullWidth
                value={asOn}
                onChange={(e) => setAsOn(e.target.value)}
                InputLabelProps={{ shrink: true }}
                FormHelperTextProps={{
                  sx: { fontSize: "0.65rem", color: INK_MUTED, mx: 0 },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="Floor"
                placeholder="Floor name"
                size="small"
                fullWidth
                value={floorSearch}
                onChange={(e) => setFloorSearch(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md="auto">
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  onClick={buildReport}
                  disabled={loading}
                  startIcon={
                    loading ? (
                      <CircularProgress size={16} sx={{ color: "#fff" }} />
                    ) : (
                      <SearchIcon />
                    )
                  }
                  sx={{
                    bgcolor: BLUE,
                    textTransform: "none",
                    fontWeight: 700,
                    boxShadow: "none",
                    "&:hover": { bgcolor: BLUE_DARK, boxShadow: "none" },
                  }}
                >
                  View
                </Button>
                <ReportSortControl
                  options={SORT_OPTIONS}
                  value={sort}
                  onChange={setSort}
                />
                <Tooltip title="Reset filters">
                  <IconButton
                    onClick={handleReset}
                    sx={{ border: `1px solid ${BORDER}`, borderRadius: 2 }}
                  >
                    <RefreshIcon sx={{ color: INK_MUTED }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Grid>
          </Grid>

          {/* ── Report options ── */}
          <Box
            sx={{
              mt: 1.5,
              pt: 1.5,
              borderTop: `1px dashed ${BORDER}`,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              columnGap: 0.5,
              rowGap: 0.25,
            }}
          >
            {[
              {
                label: "Hide Total",
                checked: hideTotal,
                onChange: () => setHideTotal((v) => !v),
              },
              {
                label: "Hide Empty Floors",
                checked: hideEmpty,
                onChange: () => setHideEmpty((v) => !v),
              },
              {
                label: "Show Loan Amount",
                checked: showAmount,
                onChange: () => setShowAmount((v) => !v),
              },
              {
                label: "Include Settled Loans",
                checked: includeSettled,
                onChange: () => setIncludeSettled((v) => !v),
              },
            ].map((opt) => (
              <FormControlLabel
                key={opt.label}
                control={
                  <Checkbox
                    size="small"
                    checked={opt.checked}
                    onChange={opt.onChange}
                    sx={{
                      py: 0.25,
                      color: INK_MUTED,
                      "&.Mui-checked": { color: BLUE },
                    }}
                  />
                }
                label={opt.label}
                sx={{
                  m: 0,
                  "& .MuiFormControlLabel-label": {
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    color: INK_SOFT,
                  },
                }}
              />
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* ── View section (results) ── */}
      <Card
        elevation={0}
        sx={{
          border: `1px solid ${BORDER}`,
          borderRadius: 3,
          bgcolor: SURFACE,
          ...reportResultsCardSx,
        }}
      >
        <Box
          sx={{
            px: { xs: 1.5, md: 2 },
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
            borderBottom: `1px solid ${BORDER}`,
            ...reportFixedSx,
          }}
        >
          <Typography sx={{ fontWeight: 700, color: INK, fontSize: "0.95rem" }}>
            Floorwise Summary on {dayjs(asOn).format("DD-MM-YYYY")}
          </Typography>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Chip
              label={`${rows.length} floor${rows.length === 1 ? "" : "s"} · ${
                totals.ticket_count
              } ticket${totals.ticket_count === 1 ? "" : "s"}`}
              size="small"
              sx={{
                bgcolor: BLUE_PALE,
                color: BLUE_DARK,
                fontWeight: 700,
                fontSize: "0.72rem",
              }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={generatePDF}
              disabled={loading || rows.length === 0}
              startIcon={<PdfIcon />}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                color: BLUE,
                borderColor: BLUE,
                "&:hover": { borderColor: BLUE_DARK, bgcolor: BLUE_PALE },
              }}
            >
              Print PDF
            </Button>
          </Stack>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress sx={{ color: BLUE }} />
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 6, px: 2 }}>
            <FloorIcon sx={{ fontSize: 44, color: BORDER, mb: 1 }} />
            <Typography sx={{ color: INK_MUTED, fontSize: "0.9rem" }}>
              No floors to show. Add floors in Masters → Floor, or adjust the
              filters and press View.
            </Typography>
          </Box>
        ) : (
          <TableContainer sx={reportTableSx}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headCellSx, width: 36 }} />
                  <TableCell sx={headCellSx}>Floor</TableCell>
                  <TableCell align="right" sx={headCellSx}>
                    No of Pawn Ticket
                  </TableCell>
                  <TableCell align="right" sx={headCellSx}>
                    Weight of Jewels
                  </TableCell>
                  <TableCell align="right" sx={headCellSx}>
                    Tare Wt.
                  </TableCell>
                  <TableCell align="right" sx={headCellSx}>
                    Extra Tare
                  </TableCell>
                  <TableCell align="right" sx={headCellSx}>
                    Total Wt.
                  </TableCell>
                  {showAmount && (
                    <TableCell align="right" sx={headCellSx}>
                      Loan Amount (₹)
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((f) => {
                  const key = String(f.floor_id ?? "none");
                  const open = expanded.has(key);
                  const hasLoans = (f.loans || []).length > 0;
                  return (
                    <React.Fragment key={key}>
                      <TableRow
                        hover
                        onClick={() => hasLoans && toggleRow(key)}
                        sx={{
                          cursor: hasLoans ? "pointer" : "default",
                          "&:hover": { bgcolor: BLUE_PALE },
                        }}
                      >
                        <TableCell sx={{ ...cellSx, px: 0.5 }}>
                          {hasLoans &&
                            (open ? (
                              <ExpandIcon
                                sx={{ fontSize: 18, color: INK_MUTED }}
                              />
                            ) : (
                              <CollapseIcon
                                sx={{ fontSize: 18, color: INK_MUTED }}
                              />
                            ))}
                        </TableCell>
                        <TableCell
                          sx={{ ...cellSx, fontWeight: 700, color: INK }}
                        >
                          {f.floor_name}
                          {f.floor_quick_no ? (
                            <Typography
                              component="span"
                              sx={{
                                ml: 0.75,
                                fontSize: "0.7rem",
                                fontWeight: 600,
                                color: INK_MUTED,
                              }}
                            >
                              ({f.floor_quick_no})
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell align="right" sx={cellSx}>
                          {f.ticket_count}
                        </TableCell>
                        <TableCell align="right" sx={cellSx}>
                          {fmtWeight(f.gross_wt)}
                        </TableCell>
                        <TableCell align="right" sx={cellSx}>
                          {fmtWeight(f.tare_wt)}
                        </TableCell>
                        <TableCell align="right" sx={cellSx}>
                          {fmtWeight(f.extra_tare_wt)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ ...cellSx, fontWeight: 600 }}
                        >
                          {fmtWeight(f.total_wt)}
                        </TableCell>
                        {showAmount && (
                          <TableCell align="right" sx={cellSx}>
                            {f.ticket_count ? fmtINR(f.loan_amount) : ""}
                          </TableCell>
                        )}
                      </TableRow>

                      {/* Drill-down: the individual tickets on this floor. */}
                      {hasLoans && (
                        <TableRow>
                          <TableCell
                            colSpan={colCount}
                            sx={{ p: 0, borderBottom: 0 }}
                          >
                            <Collapse in={open} timeout="auto" unmountOnExit>
                              <Box sx={{ px: 2, py: 1.5, bgcolor: "#F8FAFC" }}>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={headCellSx}>
                                        Ticket No
                                      </TableCell>
                                      <TableCell sx={headCellSx}>
                                        Pledge Date
                                      </TableCell>
                                      <TableCell sx={headCellSx}>
                                        Customer
                                      </TableCell>
                                      <TableCell align="right" sx={headCellSx}>
                                        Pcs
                                      </TableCell>
                                      <TableCell align="right" sx={headCellSx}>
                                        Gross Wt.
                                      </TableCell>
                                      <TableCell align="right" sx={headCellSx}>
                                        Tare Wt.
                                      </TableCell>
                                      <TableCell align="right" sx={headCellSx}>
                                        Loan Amount (₹)
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {f.loans.map((l) => (
                                      <TableRow key={l.id}>
                                        <TableCell
                                          sx={{
                                            ...cellSx,
                                            fontWeight: 700,
                                            color: INK,
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          {l.voucher_no}
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            ...cellSx,
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          {l.loan_date
                                            ? dayjs(l.loan_date).format(
                                                "DD-MM-YYYY",
                                              )
                                            : "—"}
                                        </TableCell>
                                        <TableCell sx={cellSx}>
                                          {l.customer_name || "—"}
                                          {l.customer_mobile ? (
                                            <Typography
                                              component="span"
                                              sx={{
                                                ml: 0.75,
                                                fontSize: "0.7rem",
                                                color: INK_MUTED,
                                              }}
                                            >
                                              {l.customer_mobile}
                                            </Typography>
                                          ) : null}
                                        </TableCell>
                                        <TableCell align="right" sx={cellSx}>
                                          {l.pcs || 0}
                                        </TableCell>
                                        <TableCell align="right" sx={cellSx}>
                                          {fmtWeightAlways(l.gross_wt)}
                                        </TableCell>
                                        <TableCell align="right" sx={cellSx}>
                                          {fmtWeightAlways(l.tare_wt)}
                                        </TableCell>
                                        <TableCell align="right" sx={cellSx}>
                                          {fmtINR(l.loan_amount)}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                                {/* The backend caps the drill-down list; the
                                    summary row above still counts every
                                    ticket, so say so rather than let the two
                                    silently disagree. */}
                                {f.loans.length < f.ticket_count && (
                                  <Typography
                                    sx={{
                                      mt: 1,
                                      fontSize: "0.72rem",
                                      color: INK_MUTED,
                                    }}
                                  >
                                    Showing the first {f.loans.length} of{" "}
                                    {f.ticket_count} tickets on this floor — the
                                    summary totals cover all of them.
                                  </Typography>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
              {!hideTotal && (
                <TableFooter>
                  <TableRow>
                    <TableCell sx={footBase} />
                    <TableCell sx={footBase}>Total</TableCell>
                    <TableCell align="right" sx={footBase}>
                      {totals.ticket_count}
                    </TableCell>
                    <TableCell align="right" sx={footBase}>
                      {fmtWeightAlways(totals.gross_wt)}
                    </TableCell>
                    <TableCell align="right" sx={footBase}>
                      {fmtWeightAlways(totals.tare_wt)}
                    </TableCell>
                    <TableCell align="right" sx={footBase}>
                      {fmtWeightAlways(totals.extra_tare_wt)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ ...footBase, color: SUCCESS }}
                    >
                      {fmtWeightAlways(totals.total_wt)}
                    </TableCell>
                    {showAmount && (
                      <TableCell
                        align="right"
                        sx={{ ...footBase, color: SUCCESS }}
                      >
                        {fmtINR(totals.loan_amount)}
                      </TableCell>
                    )}
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </TableContainer>
        )}
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FloorReport;
